import { useEffect, useState } from "react";
import { useAmplitudeContext } from "@/providers/amplitude-provider/amplitude-provider";
import { useCurrentUser } from "@/hooks/use-current-user";
import {
  getAnonUser,
  trackABTestConversion,
} from "@/lib/ab-testing/ab-testing.utils";
import { ExperimentClient } from "@amplitude/experiment-js-client";
import { User } from "@/providers/amplitude-provider/amplitude-provider.types";

const useABTest = (testName: string) => {
  const [variant, setVariant] = useState<string | null>(null);
  const { experiment } = useAmplitudeContext();
  const user = useCurrentUser();

  const [isLoading, setIsLoading] = useState(false);

  const trackConversion = (eventName: string, props: Record<string, any>) => {
    if (variant) {
      trackABTestConversion(testName, variant, eventName, props);
    }
  };

  const fetchExperimentForUser = (experiment: ExperimentClient, user: User) => {
    setIsLoading(true);
    experiment
      .fetch({
        user_id: user.id,
      })
      .then(() => {
        const variant = experiment.variant(testName);
        const value = variant?.value ?? null;
        setVariantWithLoading(value);
      });
  };

  const fetchExperimentForUserAsync = async (
    experiment: ExperimentClient,
    user: User,
  ) => {
    setIsLoading(true);
    await experiment.fetch({
      user_id: user.id,
    });
    const variant = experiment.variant(testName);
    const value = variant?.value ?? null;
    setVariantWithLoading(value);

    return value;
  };

  const fetchExperimentForAnon = (experiment: ExperimentClient) => {
    setIsLoading(true);
    const anonUser = getAnonUser();
    experiment.fetch(anonUser).then(() => {
      const variant = experiment.variant(testName);
      const value = variant?.value ?? null;
      setVariantWithLoading(value);
    });
  };

  const fetchExperimentForAnonAsync = async (experiment: ExperimentClient) => {
    setIsLoading(true);
    const anonUser = getAnonUser();
    await experiment.fetch(anonUser);
    const variant = experiment.variant(testName);
    const value = variant?.value ?? null;
    setVariantWithLoading(value);

    return value;
  };

  const setVariantWithLoading = (value: string | null) => {
    setVariant(value);
    setIsLoading(false);
  };

  return {
    testName,
    user,
    experiment,
    variant,
    isLoading,
    trackConversion,
    fetchExperimentForUserAsync,
    fetchExperimentForUser,
    fetchExperimentForAnonAsync,
    fetchExperimentForAnon,
  };
};

export const useABTestTracking = (testName: string) => {
  const {
    experiment,
    user,
    fetchExperimentForUser,
    fetchExperimentForAnon,
    trackConversion,
    variant,
    isLoading,
  } = useABTest(testName);

  useEffect(() => {
    if (!experiment) {
      return;
    }

    if (!user) {
      fetchExperimentForAnon(experiment);
      return;
    }

    fetchExperimentForUser(experiment, user);
  }, [testName, user, experiment]);

  return { variant, isLoading, trackConversion };
};

export const useABTestTrackingAfter = (testName: string) => {
  const {
    experiment,
    user,
    fetchExperimentForUserAsync,
    fetchExperimentForAnonAsync,
    trackConversion,
    variant,
  } = useABTest(testName);

  const fetchExperiment = async (experiment: ExperimentClient, user?: User) => {
    if (!user) {
      return fetchExperimentForAnonAsync(experiment);
    }

    return fetchExperimentForUserAsync(experiment, user);
  };

  return { variant, trackConversion, fetchExperiment, user, experiment };
};
